import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { SabreLogo } from '../components/SabreLogo';
import { SparkFooter } from '../components/SparkFooter';
import { SparkInput } from '../components/SparkInput';
import { SparkMessage } from '../components/SparkMessage';
import { useAuth } from '../context/AuthContext';
import { ApiError, changePassword } from '../lib/api';

/**
 * Shown after signing in with a temporary password, and reachable any time a
 * user wants to rotate their own credentials.
 */
export function ChangePasswordPage() {
  const { user, mustChangePassword, applySession, signOut } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError('');

    const errors: Record<string, string> = {};
    if (!currentPassword) errors.currentPassword = 'Enter your current password.';
    if (!newPassword) errors.newPassword = 'Enter a new password.';
    if (newPassword && newPassword !== confirmPassword) {
      errors.confirmPassword = 'The two passwords do not match.';
    }
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      const session = await changePassword(currentPassword, newPassword);
      applySession(session);
      navigate('/', { replace: true });
    } catch (error) {
      if (error instanceof ApiError) {
        setFormError(error.message);
        setFieldErrors(error.fields ?? {});
      } else {
        setFormError('The password could not be changed. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (mustChangePassword) {
      await signOut();
      navigate('/login', { replace: true, state: { loggedOut: true } });
      return;
    }
    navigate('/', { replace: true });
  };

  return (
    <div className="spark-splash-screen">
      <section className="spark-splash-screen__content">
        <div className="spark-center-block">
          <SabreLogo width={286} className="spark-logo--md" />
          <h2 className="spark-splash-screen__heading">Agency Workspace</h2>

          <div className="spark-panel">
            <div className="spark-panel__content">
              {mustChangePassword && (
                <SparkMessage variant="warning">
                  Your password was issued by an administrator. Choose a new one to continue.
                </SparkMessage>
              )}
              {formError && <SparkMessage variant="error">{formError}</SparkMessage>}

              <h3 className="spark-panel__heading">Change password</h3>
              <p className="field-hint">
                Signed in as <strong>{user?.userId}</strong>. Use at least 8 characters mixing upper case, lower
                case, digits and symbols.
              </p>

              <form onSubmit={handleSubmit} noValidate>
                <SparkInput
                  label="Current password"
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  error={fieldErrors.currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  disabled={submitting}
                  autoFocus
                />
                <SparkInput
                  label="New password"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  error={fieldErrors.newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  disabled={submitting}
                />
                <SparkInput
                  label="Confirm new password"
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  error={fieldErrors.confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  disabled={submitting}
                />

                <div className="spark-mar-t-1">
                  <button
                    type="submit"
                    className="spark-btn spark-btn--lg spark-btn--primary spark-btn--block"
                    disabled={submitting}
                  >
                    {submitting ? 'Saving…' : 'Change password'}
                  </button>
                </div>
              </form>

              <div className="login-links">
                <button type="button" className="spark-btn-link" onClick={handleCancel}>
                  {mustChangePassword ? 'Sign out instead' : 'Back to workspace'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <SparkFooter />
    </div>
  );
}
